import { useState, useEffect, memo } from 'react';
import { ApiError } from 'aws-amplify/api';
import Layout from '../mui-components/Layout';
import Button from '@mui/material/Button';
import {
  Box,
  Table,
  Paper,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Typography,
  TableContainer,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { pageTitles } from '../data/pageTitles';
import { useGlobalContext } from '../context/GlobalStore';
import UserTableHead from '../mui-components/UserTableHead';
import UserRoleCell from '../mui-components/Account/UserRoleCell';
import ActionsCell from '../mui-components/Account/ActionsCell';
import CreateUserModal from '../mui-components/Account/CreateUserModal';
import UserAlertModal from '../mui-components/Account/UserAlertModal';
import ErrorAlertModal from '../mui-components/ErrorAlertModal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import apiClient from '../utils/apiClient';

const Account = ({ getAccount }) => {
  const { state } = useGlobalContext();
  const [openCreateUser, setOpenCreateUser] = useState(false);
  document.title = pageTitles.account;
  const [openAlert, setOpenAlert] = useState(false);
  const [openViewUser, setOpenViewUser] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: '',
  });
  const [accountUsers, setAccountUsers] = useState([]);
  const [deleteUserIndex, setDeleteUserIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0);
  const [allowCreateUser, setAllowCreateUser] = useState(true);
  const [createUserMessage, setCreateUserMessage] = useState("");
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  async function createUser(e) {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiClient.post(
        '/user',
        {
          action: 'create',
          name: newUser.name,
          email: newUser.email,
          role: newUser.role,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );
    
      if (response) {
        toggleCreateUserModal();
        setSnackbarMessage("User created successfully");
        setOpenSnackbar(true);
        setIsLoading(false);
        await getAccount(state.token.toString());
        setNewUser({
          name: '',
          email: '',
          role: '',
        });
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`)
          const parsedBody = JSON.parse(body);
          errorMessage = parsedBody;
          if (parsedBody.message) {
            errorMessage = parsedBody.message;
          }
        }
      } else {
        // standard error
        errorMessage = error.message
      }
      setAlertMessage(`Error creating user: ${errorMessage}`)
      setAlertTitle("Error");
      setOpenErrorAlert(true);
      setIsLoading(false);
      toggleCreateUserModal();
      setNewUser({
        name: '',
        email: '',
        role: '',
      });
    }
  }

  async function deleteUser(i) {
    setIsLoading(true);
    try { 
      const response = await apiClient.post(
        '/user',
        {
          action: 'delete',
          email: accountUsers[i].email,
        },
        {
          retryOptions: { maxRetries: 0 }
        },
        true
      );
      if (response) {
        setOpenAlert(false);
        setSnackbarMessage("User deleted successfully");
        setOpenSnackbar(true);
        setIsLoading(false);
        await getAccount(state.token.toString());
      }
    } catch (error) {
      let errorMessage = '';
      if (error instanceof ApiError) {
        if (error.response) {
          const { statusCode, headers, body} = error.response;
          console.log(`Received ${statusCode} error response with payload ${body}`)
          const parsedBody = JSON.parse(body);
          errorMessage = parsedBody;
          if (parsedBody.message) {
            errorMessage = parsedBody.message;
          }
        }
      } else {
        // standard error
        errorMessage = error.message
      }
      setAlertMessage(`Error deleting user: ${errorMessage}`)
      setAlertTitle("Error");
      setOpenErrorAlert(true);
      setIsLoading(false);
    }
  }

  const handleCreateUser = (e) => {
    createUser(e);
  };
  const toggleCreateUserModal = () => {
    setOpenCreateUser(!openCreateUser);
  };
  const viewUser = (i) => {
    setCurrentAccountIndex(i);
    setOpenViewUser(true);
  };
  const editUser = (i) => {
    setOpenEditModal(!openEditModal);
    setCurrentAccountIndex(i);
  };
  const alertDeleteUser = (i) => {
    setOpenAlert(!openAlert);
    setDeleteUserIndex(i);
  };
  const deleteUserByIndex = () => {
    deleteUser(deleteUserIndex);
  };
  const handleCloseAlert = () => {
    setOpenAlert(!openAlert);
    setDeleteUserIndex(null);
  };
  useEffect(() => {
    // update accountuser
    if (state?.account?.account_users?.length > 0) {
      const copy = [...state.account.account_users];
      setAccountUsers(copy);
    }

    if (state?.account?.account_users?.length < 10) {
      setAllowCreateUser(true);
      setCreateUserMessage("");
    } else {
      setAllowCreateUser(false);
      setCreateUserMessage("User limit exceeded.")
    }
    if (!state?.policies?.hasOwnProperty("Admin")) {
      setAllowCreateUser(false);
      setCreateUserMessage("Only administrators can create new users.")
    }
  }, [state.account]);

  return (
    <Layout title="Users">
      <Snackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {openCreateUser && (
        <CreateUserModal
          open={true}
          handleClose={toggleCreateUserModal}
          handleCreateUser={handleCreateUser}
          newUser={newUser}
          setNewUser={setNewUser}
          isLoading={isLoading}
        />
      )}
      {openAlert && (
        <UserAlertModal
          open={true}
          handleClose={handleCloseAlert}
          deleteUser={deleteUserByIndex}
          userToDelete={accountUsers[deleteUserIndex]}
          isLoading={isLoading}
        />
      )}
      <ErrorAlertModal
        open={openErrorAlert}
        handleClose={() => setOpenErrorAlert(false)}
        message={alertMessage}
        title={alertTitle}
      />
      <Box>
        <Box display="flex">
          <Typography variant="body1" color="GrayText" mr={2}>
            Customer Name:
          </Typography>
          <Typography variant="body1">
            {!!state?.account?.pylo_accounts?.length &&
              state.account.pylo_accounts[0].customer_name}
          </Typography>
        </Box>

        <TableContainer style={{ marginTop: '30px' }} component={Paper}>
          <Table>
            <UserTableHead
              list={['Name', 'Email', 'User Role', 'Actions']}
            />
            <TableBody>
              {!!accountUsers?.length &&
                accountUsers.map((user, i) => {
                  const role = user.user_role;
                  return (
                    <TableRow key={user.email}>
                      <TableCell>{user['user_name']}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <UserRoleCell roles={[role]} />

                      <ActionsCell
                        user={user}
                        deleteUser={() => alertDeleteUser(i)}
                        viewUser={() => viewUser(i)}
                        editUser={() => editUser(i)}
                      />
                      <TableCell>
                        {!!state?.account &&
                          i >= state.account.account_users.length && (
                            <IconButton
                              onClick={() => createUser(i)}
                              aria-label={`create this user`}
                            >
                              <AddIcon />
                            </IconButton>
                          )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {state.account?.account_users?.length >= 1 && (
          <Box mt={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          <Button
            disabled={!allowCreateUser}
            onClick={toggleCreateUserModal}
            variant="contained"
            sx={{ float: 'right' }}
          >
            Create User
          </Button>
          {!allowCreateUser && (
            <Typography variant="caption" color="textSecondary" sx={{ textAlign: 'right', maxWidth: 250 }}>
              {createUserMessage}
            </Typography>
          )}
          
        </Box>
        )}
      </Box>
    </Layout>
  );
};

export default memo(Account);
